import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import type { LayoutItem } from 'react-grid-layout';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';

import { defaultLayoutState, defaultLayouts, persistDebounceMs } from './constants';
import type { PanelId, TradeLayoutState } from './types';
import { loadLayout } from './validate';

/**
 * Compare two layout states semantically (order-insensitive for layouts array).
 * JSON.stringify is order-sensitive and would produce false positives after any drag.
 */
function isLayoutEqual(a: TradeLayoutState, b: TradeLayoutState): boolean {
  if (a.version !== b.version) return false;
  if (a.hiddenPanels.length !== b.hiddenPanels.length) return false;
  if (!a.hiddenPanels.every((p) => b.hiddenPanels.includes(p))) return false;
  if (a.layouts.length !== b.layouts.length) return false;

  // Match by id instead of sorting — avoids array copy + sort on every comparison.
  return a.layouts.every((itemA) => {
    const itemB = b.layouts.find((l) => l.i === itemA.i);
    if (!itemB) return false;
    return itemA.x === itemB.x && itemA.y === itemB.y && itemA.w === itemB.w && itemA.h === itemB.h;
  });
}

export const tradeLayoutBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom } = bunja.use(authBunja);

  // Synchronous initialization from localStorage (matches pipModeConfigBunja pattern).
  // atomWithDefault reads localStorage on first atom access, preventing a layout flash.
  // It creates a writable atom so store.set() works for updateLayouts/closePanel/etc.
  const layoutStateBaseAtom = atomWithDefault<TradeLayoutState>((get) => {
    const user = get(userAtom);
    if (!user) return defaultLayoutState;
    const storageKey = getFlipsterConfigsV1Key(user.userId, 'tradeLayout');
    const raw = getLocalStorage(storageKey);
    return loadLayout(raw) ?? defaultLayoutState;
  });

  const layoutStateAtom = atom(
    (get) => get(layoutStateBaseAtom),
    (_get, set, value: TradeLayoutState) => {
      set(layoutStateBaseAtom, value);
    },
  );

  // Derived: is current layout different from default?
  const isCustomizedAtom = atom((get) => {
    return !isLayoutEqual(get(layoutStateAtom), defaultLayoutState);
  });

  // Debounced persist
  let persistTimer: ReturnType<typeof setTimeout> | null = null;

  bunja.effect(() => {
    return () => {
      if (persistTimer) clearTimeout(persistTimer);
    };
  });

  const saveLayoutToStorage = (state: TradeLayoutState) => {
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      const user = store.get(userAtom);
      if (!user) return;
      const storageKey = getFlipsterConfigsV1Key(user.userId, 'tradeLayout');
      setLocalStorage(storageKey, state);
    }, persistDebounceMs);
  };

  // Actions
  const updateLayouts = (newLayouts: LayoutItem[]) => {
    // Don't update before auth loads — atomWithDefault hasn't read localStorage yet.
    // Writing now would overwrite the default getter, preventing restore on user load.
    if (!store.get(userAtom)) return;

    const current = store.get(layoutStateAtom);
    // onLayoutChange only returns visible items. Merge with hidden panels' stored layouts.
    const hiddenLayouts = current.layouts.filter((l) => current.hiddenPanels.includes(l.i as PanelId));
    const mergedLayouts = [...newLayouts, ...hiddenLayouts];
    const updated: TradeLayoutState = { ...current, layouts: mergedLayouts };
    // Skip if layout didn't actually change (e.g., onLayoutChange fires on mount)
    if (isLayoutEqual(updated, current)) return;
    store.set(layoutStateAtom, updated);
    saveLayoutToStorage(updated);
  };

  const closePanel = (id: PanelId) => {
    const current = store.get(layoutStateAtom);
    if (current.hiddenPanels.includes(id)) return;

    // Keep layout item in layouts so size/position is preserved when re-opened.
    const updated: TradeLayoutState = {
      ...current,
      hiddenPanels: [...current.hiddenPanels, id],
    };
    store.set(layoutStateAtom, updated);
    saveLayoutToStorage(updated);
  };

  const openPanel = (id: PanelId) => {
    const current = store.get(layoutStateAtom);
    if (!current.hiddenPanels.includes(id)) return;

    // If the panel's layout was preserved, just unhide it.
    // If missing (e.g. migrated from old version), add with default values.
    const hasLayout = current.layouts.some((l) => l.i === id);
    const layouts = hasLayout
      ? current.layouts
      : [
          ...current.layouts,
          {
            ...(defaultLayouts.find((l) => l.i === id) ?? { i: id, x: 0, y: Infinity, w: 6, h: 10, minW: 4, minH: 4 }),
            y: Infinity,
          },
        ];

    const updated: TradeLayoutState = {
      ...current,
      layouts,
      hiddenPanels: current.hiddenPanels.filter((p) => p !== id),
    };
    store.set(layoutStateAtom, updated);
    saveLayoutToStorage(updated);
  };

  const resetLayout = () => {
    store.set(layoutStateAtom, defaultLayoutState);
    const user = store.get(userAtom);
    if (user) {
      const storageKey = getFlipsterConfigsV1Key(user.userId, 'tradeLayout');
      setLocalStorage(storageKey, defaultLayoutState);
    }
  };

  return {
    layoutStateAtom,
    isCustomizedAtom,
    updateLayouts,
    closePanel,
    openPanel,
    resetLayout,
  };
});

tradeLayoutBunja.debugLabel = 'tradeLayoutBunja';
